function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}
var dispatchCustomEvent = function dispatchCustomEvent(eventName) {
  var data = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var detail = {
    detail: data
  };
  var event = new CustomEvent(eventName, data ? detail : null);
  document.dispatchEvent(event);
};
/**
 * Publish a Shopify standard event (shopify:* DOM events for analytics apps).
 * The event classes are loaded async from Shopify's CDN via a module script in
 * theme.liquid — no-op until the module is available, and never let an
 * analytics payload error break a commerce flow.
 * https://shopify.dev/docs/storefronts/themes/best-practices/standard-events
 */
var publishStandardEvent = function publishStandardEvent(eventName, payload, target) {
  const SE = window.Shopify && window.Shopify.StandardEvents;
  if (!SE || !SE[eventName]) return;
  try {
    (target || document).dispatchEvent(new SE[eventName](payload || {}));
  } catch (e) {
    console.error('Standard event failed:', eventName, e);
  }
};
/**
 * Run a DOM update inside a View Transition when the browser supports it
 * (and the user hasn't asked for reduced motion) — otherwise apply the
 * update directly. Progressive enhancement only; callers must not rely
 * on the transition happening.
 *
 * Skipped while an overlay is open: the transition layer paints above
 * ALL z-indexes, so a named element (eg. the product grid) would flash
 * in front of the click-capture dim and any open drawer.
 */
var withViewTransition = function withViewTransition(update) {
  if (
    !document.startViewTransition ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    OverlayManager.hasOpen()
  ) {
    update();
    return;
  }
  document.startViewTransition(update);
};
window.recentlyViewedIds = [];

// Send internal collection links to Shopify with the preferred availability
// state already in the URL. This lets Shopify render the correct product grid
// on the first request instead of filtering it again after the page appears.
(function () {
  const availabilityParam = 'filter.v.availability';
  const availabilityOptOutParam = 'show_unavailable';

  const prepareCollectionLink = (event) => {
    const link = event.target.closest?.('a[href]');
    if (!link || link.hasAttribute('download')) return;

    const rawHref = link.getAttribute('href');
    if (!rawHref || rawHref.startsWith('#')) return;

    const destination = new URL(link.href, window.location.origin);
    const pathParts = destination.pathname.split('/').filter(Boolean);
    const collectionsIndex = pathParts.indexOf('collections');
    const isCollectionLanding =
      collectionsIndex !== -1 &&
      pathParts.length === collectionsIndex + 2;

    if (
      destination.origin !== window.location.origin ||
      !isCollectionLanding ||
      !pathParts[collectionsIndex + 1]
    ) {
      return;
    }

    const isThemeDefault =
      link.dataset.defaultAvailabilityLink === 'true';
    const hasExplicitPreference =
      destination.searchParams.has(availabilityParam) ||
      destination.searchParams.has(availabilityOptOutParam);

    if (hasExplicitPreference && !isThemeDefault) return;

    const showUnavailable =
      new URLSearchParams(window.location.search).get(availabilityOptOutParam) === '1';

    destination.searchParams.delete(availabilityParam);
    destination.searchParams.delete(availabilityOptOutParam);
    destination.searchParams.set(
      showUnavailable ? availabilityOptOutParam : availabilityParam,
      '1'
    );

    link.dataset.defaultAvailabilityLink = 'true';
    link.href = destination.toString();
  };

  ['pointerdown', 'focusin', 'click'].forEach((eventName) => {
    document.addEventListener(eventName, prepareCollectionLink, true);
  });
})();

// --- Shopify standard events: adapters over the theme's internal event bus ---
(function () {
  const SE = () => window.Shopify && window.Shopify.StandardEvents;
  const ajaxCart = (cart) => SE().CartLinesUpdateEvent.createCartFromAjaxResponse(cart);

  // shopify:cart:view — cart drawer opened. The drawer HTML carries no cart
  // JSON, so fetch /cart.js (tiny, user-triggered) for an accurate payload.
  document.addEventListener('cart-drawer:open', () => {
    if (!SE()) return;
    fetch(`${theme.routes.cart_url}.js`, { headers: { 'Accept': 'application/json' } })
      .then((response) => response.json())
      .then((cart) => {
        if (!cart || !cart.token) return;
        publishStandardEvent('CartViewEvent', {
          cart: ajaxCart(cart),
          context: 'dialog'
        });
      })
      .catch(() => { /* analytics only — never surface */ });
  });

  // shopify:cart:lines-update (add) — /cart/add.js responds with the added
  // line(s) only, so the promise resolves without a full cart payload.
  document.addEventListener('cart:item-added', (e) => {
    const item = e.detail && e.detail.product;
    publishStandardEvent('CartLinesUpdateEvent', {
      action: 'add',
      lines: item && item.variant_id ? [{ merchandiseId: item.variant_id, quantity: item.quantity || 1 }] : undefined,
      promise: Promise.resolve({ cart: null })
    });
  });

  // shopify:cart:lines-update (update/remove) — /cart/change.js responds with
  // the full AJAX cart, already present in the internal event detail.
  document.addEventListener('line-item:change:end', (e) => {
    if (!SE()) return;
    const cart = e.detail && e.detail.cart;
    const quantity = e.detail ? parseInt(e.detail.quantity, 10) : NaN;
    publishStandardEvent('CartLinesUpdateEvent', {
      action: quantity === 0 ? 'remove' : 'update',
      promise: Promise.resolve({
        cart: cart && cart.token && cart.items ? ajaxCart(cart) : null
      })
    });
  });

  // shopify:cart:error — add-to-cart rejected (out of stock, limits, etc.)
  document.addEventListener('product:variant-error', (e) => {
    const detail = e.detail || {};
    publishStandardEvent('CartErrorEvent', {
      errors: [{ message: detail.message || detail.errors || 'Cart error' }]
    });
  });
})();

const OverlayManager = {
  stack: [],
  beforeCloseHandlers: new Map(),
  returnFocus: new WeakMap(),

  // A drawer is a layer over the page, so the back gesture should peel the layer off rather
  // than throw the visitor off the page with their cart still open (GLB-004). On a phone back
  // is the most common way out of anything, so this is the difference between a drawer that
  // behaves like the rest of the platform and one that punishes the habit.
  //
  // One entry per open panel, counted here rather than read back from history, because
  // history.state cannot tell us whose entry it is once another script has pushed its own.
  // _selfPops counts the pops WE asked for (a close by tap or key calls history.back to keep
  // the entry count honest); the popstate handler consumes those before it treats a pop as
  // the visitor's, which is what keeps two stacked panels from both closing on one back press.
  _historyDepth: 0,
  _selfPops: 0,

  // The dim covers the page, not the panel (merchant, 18 Sep). A glass panel shows what is behind
  // it, and the dim ran under the panel as well, so the panel was looking at a darkened page and
  // drew grey. This writes how much of each side the open panels cover onto the dim, and the
  // stylesheet cuts the dim back by that much. Measured, not assumed: widths differ by panel and
  // by viewport, and a panel's side is read from where it is anchored, which holds while it is
  // still off screen. The dim shares the panels' 250ms curve, so the cut and the panel's edge
  // travel together; PanelDrag.paint keeps them together under a finger.
  syncDim() {
    const scrim = document.querySelector('.click-capture');
    if (!scrim) return;
    let left = 0;
    let right = 0;
    this.stack.forEach((panel) => {
      const width = panel.getBoundingClientRect().width;
      if (getComputedStyle(panel).left === '0px') left = Math.max(left, width);
      else right = Math.max(right, width);
    });
    scrim.style.setProperty('--ktechs-dim-left', `${left}px`);
    scrim.style.setProperty('--ktechs-dim-right', `${right}px`);
  },

  open(panel) {
    if (!panel || this.stack.includes(panel)) return;
    this.stack.push(panel);
    this.returnFocus.set(panel, document.activeElement);
    panel.classList.add('active');
    panel.inert = false;
    document.body.classList.add('open-cc');
    this.syncDim();
    panel.querySelector('.side-panel-close')?.focus();
    // searchParams mirrors what facets.js writes into its own entries. Its popstate handler
    // reads event.state.searchParams and re-renders the grid when the value differs, so an
    // entry of ours without that key would wipe the visitor's filters the moment history
    // moved through it. The URL is left alone: no third argument, so no /# in the bar.
    history.pushState({ ktechsPanel: true, searchParams: window.location.search.slice(1) }, '');
    this._historyDepth += 1;
    dispatchCustomEvent('panel:open', { panel });
  },

  close(panel) {
    if (!panel) return;
    const handler = this.beforeCloseHandlers.get(panel);
    if (handler) {
      handler(() => this._close(panel));
    } else {
      this._close(panel);
    }
  },

  _close(panel) {
    const index = this.stack.indexOf(panel);
    if (index > -1) this.stack.splice(index, 1);
    panel.classList.remove('active');
    panel.inert = true;
    if (!this.stack.length) {
      document.body.classList.remove('open-cc');
    }
    this.syncDim();
    // Return focus to the element that opened the overlay — but only when
    // focus is still inside the panel (or lost to <body>), so we never
    // steal focus the user has already moved elsewhere.
    const opener = this.returnFocus.get(panel);
    if (opener && document.contains(opener) && (panel.contains(document.activeElement) || document.activeElement === document.body)) {
      opener.focus();
    }
    this.returnFocus.delete(panel);
    // Give back the entry this panel took, so closing by tap, key or swipe leaves history the
    // length it was before the panel opened. Without this, three opens and three taps would
    // leave three dead entries and the visitor would press back three times to go anywhere.
    //
    // Only when this panel was actually on the stack. _close is reachable for a panel that has
    // already closed (the drag controller commits a close that Escape may have beaten it to),
    // and popping there would spend an entry this panel never took, sending the visitor back a
    // real page for a drawer that was already gone.
    //
    // And only while OUR entry is still the one on top. facets.js pushes an entry of its own
    // every time a filter changes, so with the filter drawer open the stack goes: page, panel,
    // filter. Popping blind there threw away the filter the visitor had just set and put them
    // back on the unfiltered page, which is a far worse bug than the one this fix exists for.
    // When something else is on top, the entry is left where it is: a later back press lands
    // on it, finds no panel open, and closeLast does nothing.
    const ours = history.state && history.state.ktechsPanel;
    if (index > -1 && ours && this._historyDepth > 0 && !this._poppingFromHistory) {
      this._historyDepth -= 1;
      this._selfPops += 1;
      history.back();
    }
    dispatchCustomEvent('panel:closed', { panel });
  },

  closeLast() {
    const panel = this.stack[this.stack.length - 1];
    if (panel) this.close(panel);
  },

  registerBeforeClose(panel, handler) {
    this.beforeCloseHandlers.set(panel, handler);
  },

  isOpen(panel) {
    return this.stack.includes(panel);
  },

  hasOpen() {
    return this.stack.length > 0;
  },

  // Keep Tab/Shift+Tab cycling inside the top-most open overlay. One
  // document-level listener covers every drawer/modal the manager opens.
  trapFocus(e) {
    if (e.key !== 'Tab' || !this.stack.length) return;
    const panel = this.stack[this.stack.length - 1];
    const focusables = Array.from(panel.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, iframe, [tabindex]:not([tabindex="-1"])'
    )).filter((el) => el.getClientRects().length > 0);

    if (!focusables.length) {
      e.preventDefault();
      return;
    }

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;

    if (!panel.contains(active)) {
      e.preventDefault();
      (e.shiftKey ? last : first).focus();
    } else if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }
};
document.addEventListener('keydown', (e) => OverlayManager.trapFocus(e));

window.addEventListener('popstate', () => {
  // Ours, from _close: the entry is already accounted for and the panel is already closing.
  if (OverlayManager._selfPops > 0) {
    OverlayManager._selfPops -= 1;
    return;
  }
  if (!OverlayManager._historyDepth) return;
  OverlayManager._historyDepth -= 1;
  // _close gives the entry back by calling history.back, which is exactly wrong here: the
  // visitor already spent this entry. The flag tells it to let this one go.
  OverlayManager._poppingFromHistory = true;
  OverlayManager.closeLast();
  OverlayManager._poppingFromHistory = false;
});

/**
 * PanelDrag: the drawers follow the finger and can be thrown shut (GLB-003).
 *
 * Every panel in the theme moved only by gaining a class, with a 250ms CSS transition doing the
 * travel. A transition cannot be grabbed: it runs from where it started to where it was told,
 * and a finger arriving in the middle has nothing to take hold of. So the gesture and the
 * release are owned here, by the pointer and then by a spring, and the class is left to handle
 * taps and keys exactly as before.
 *
 * It attaches on panel:open, so every panel OverlayManager knows about gets this for free:
 * menu, search, cart, product, facets, pickup and address.
 */
const PanelDrag = {
  // How far a finger must travel before this claims the gesture. Below it, nothing here runs
  // and a tap stays a tap; the axis of that first movement decides whether the panel or its
  // own scroller owns the rest, and once decided it does not change hands.
  THRESHOLD: 10,
  // Fraction of the panel's width past which a release closes rather than returns. The release
  // is judged on where the throw was going, not where the finger stopped, so this is compared
  // against the projected endpoint.
  COMMIT: 0.5,
  RESPONSE: 0.3,

  drag: null,
  spring: null,

  // Apple's deceleration projection (the exponential-decay form, not the v^2/2a of a physics
  // textbook): where a flick would come to rest if it were left to slide. A short fast flick
  // therefore closes a panel it never actually crossed, which is what makes a throw feel like
  // a throw rather than a nudge.
  project(velocity, rate = 0.998) {
    return (velocity / 1000) * rate / (1 - rate);
  },

  // Progressive resistance past the open edge. A hard stop reads as frozen; this reads as
  // responsive with nothing more to give.
  rubberband(over, dimension, c = 0.55) {
    return (over * dimension * c) / (dimension + c * Math.abs(over));
  },

  // Critically damped, solved rather than stepped, so it is exact at any frame rate and cheap.
  // Damping 1.0 because a drawer that overshoots its own closed position wobbles on the way
  // back to rest, and a shop's cart is not the place for a flourish. `response` is the time to
  // reach the target, not a duration: the motion ends when it is done.
  springTo(from, to, velocity, onFrame, onDone) {
    const w = (2 * Math.PI) / this.RESPONSE;
    const a = from - to;
    const b = velocity + w * a;
    const start = performance.now();
    let raf = 0;
    const tick = (now) => {
      const t = (now - start) / 1000;
      const decay = Math.exp(-w * t);
      const x = to + (a + b * t) * decay;
      const v = decay * (b - w * a - w * b * t);
      if (Math.abs(x - to) < 0.5 && Math.abs(v) < 5) {
        onFrame(to);
        onDone();
        return;
      }
      onFrame(x);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return { cancel: () => cancelAnimationFrame(raf) };
  },

  attach(panel) {
    if (panel.dataset.ktechsDrag) return;
    panel.dataset.ktechsDrag = 'on';
    panel.addEventListener('pointerdown', (e) => this.down(panel, e));
    panel.addEventListener('pointermove', (e) => this.move(e));
    panel.addEventListener('pointerup', (e) => this.up(e));
    // A cancelled pointer is not a release: the system took the finger away (a call arrived,
    // a system gesture won), and the visitor never decided anything. Returning the panel is
    // the only honest reading of that, so the cancel path is told not to judge the throw.
    panel.addEventListener('pointercancel', (e) => this.up(e, true));
  },

  // Three panels enter from the left and the rest from the right, and [dir=rtl] flips the menu.
  // Reading the rendered box rather than the class list means this cannot drift out of step
  // with the CSS: an open panel is flush against the edge it came from.
  exitSign(panel) {
    return panel.getBoundingClientRect().left <= 1 ? -1 : 1;
  },

  // A drag that starts on something with its own gesture would be two controls fighting over
  // one finger. The price slider and the gallery both track horizontally, and a text field
  // needs its own caret drag.
  claimable(target) {
    return !target.closest(
      'input, textarea, select, .noUi-target, .flickity-enabled, [data-no-panel-drag]'
    );
  },

  down(panel, e) {
    // Mouse drags are left alone: on a pointer device the panel closes by click, and claiming
    // the button here would take text selection away for no gain.
    if (!e.isPrimary || e.pointerType === 'mouse' || !this.claimable(e.target)) return;
    if (!OverlayManager.isOpen(panel)) return;

    // Interruptibility: a finger arriving during the settle takes over from where the panel
    // actually is on screen, never from where it was heading (skill section 3).
    let offset = 0;
    if (this.spring) {
      this.spring.cancel();
      this.spring = null;
      offset = Math.abs(new DOMMatrixReadOnly(getComputedStyle(panel).transform).m41);
    }

    this.drag = {
      panel,
      id: e.pointerId,
      sign: this.exitSign(panel),
      width: panel.getBoundingClientRect().width,
      startX: e.clientX,
      startY: e.clientY,
      offset,
      base: offset,
      claimed: false,
      released: false,
      history: [{ x: e.clientX, t: performance.now() }]
    };
  },

  move(e) {
    const d = this.drag;
    if (!d || e.pointerId !== d.id) return;

    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;

    if (!d.claimed) {
      if (Math.abs(dx) < this.THRESHOLD && Math.abs(dy) < this.THRESHOLD) return;
      // The first real movement decides, once. A mostly-vertical start belongs to the panel's
      // own scroller and is never taken back, or a list would stutter every time a thumb
      // wandered sideways on its way down.
      if (Math.abs(dy) >= Math.abs(dx)) {
        this.drag = null;
        return;
      }
      d.claimed = true;
      d.panel.setPointerCapture(d.id);
      d.panel.style.transition = 'none';
      const scrim = document.querySelector('.click-capture');
      if (scrim) scrim.style.transition = 'none';
    }

    d.history.push({ x: e.clientX, t: performance.now() });
    if (d.history.length > 6) d.history.shift();

    // Toward the exit edge the panel is under the finger exactly. Past its open position there
    // is nothing behind it to reveal, so it resists instead of following.
    const along = d.base + dx * d.sign;
    d.offset = along >= 0 ? along : -this.rubberband(-along, d.width);
    this.paint(d.panel, d.sign, d.offset, d.width);
    e.preventDefault();
  },

  paint(panel, sign, offset, width) {
    // Written with priority because the reduced-motion block in ktechs-brand.css pins the
    // panels to transform: none !important, and a finger has to win against that: the setting
    // is about motion we chose to add, and this movement is the visitor's own hand.
    panel.style.setProperty('transform', `translateX(${sign * offset}px)`, 'important');
    const scrim = document.querySelector('.click-capture');
    // The dim lifts with the panel, so the two read as one layer being taken off the page
    // rather than a panel sliding across a backdrop that stays put.
    if (scrim) {
      scrim.style.opacity = String(Math.max(0, 1 - Math.max(0, offset) / width));
      // And its edge stays on the panel's edge (see OverlayManager.syncDim): what the panel has
      // uncovered is dimmed page again, not a bright strip left where the panel used to be.
      scrim.style.setProperty(sign < 0 ? '--ktechs-dim-left' : '--ktechs-dim-right', `${Math.max(0, width - Math.max(0, offset))}px`);
    }
  },

  clear(panel) {
    panel.style.removeProperty('transform');
    panel.style.removeProperty('transition');
    const scrim = document.querySelector('.click-capture');
    if (scrim) {
      scrim.style.removeProperty('opacity');
      scrim.style.removeProperty('transition');
    }
    // Back to what the open panels cover: the full width if this one returned, nothing if it left.
    OverlayManager.syncDim();
  },

  up(e, cancelled = false) {
    const d = this.drag;
    if (!d || e.pointerId !== d.id || d.released) return;
    d.released = true;
    this.drag = null;
    if (!d.claimed) return;

    // Velocity from the tail of the move history rather than the last pair of points: one
    // frame is noise, and a finger that stops dead before lifting should not read as a throw.
    const last = d.history[d.history.length - 1];
    const first = d.history[0];
    const elapsed = (last.t - first.t) / 1000;
    const velocity = cancelled ? 0 : (elapsed > 0 ? ((last.x - first.x) / elapsed) * d.sign : 0);

    const projected = d.offset + this.project(velocity);
    const closing = !cancelled && projected > d.width * this.COMMIT;
    const target = closing ? d.width : 0;

    const finish = () => {
      if (!closing) {
        this.clear(d.panel);
        this.spring = null;
        return;
      }
      // Wait for the panel to actually report closed before dropping the inline styles.
      // OverlayManager.close can be gated by a beforeClose handler that waits on a transition
      // of its own (the product drawer does exactly that above 1069px, which includes touch
      // tablets), and clearing the transform while that ran would snap the panel back to open
      // and leave it sitting there until the handler finished.
      const clear = () => this.clear(d.panel);
      if (!OverlayManager.isOpen(d.panel)) {
        clear();
      } else {
        document.addEventListener('panel:closed', function once(ev) {
          if (ev.detail.panel !== d.panel) return;
          document.removeEventListener('panel:closed', once);
          clear();
        });
        OverlayManager.close(d.panel);
        // A handler that never calls done would otherwise strand the inline styles forever.
        setTimeout(clear, 1200);
      }
      this.spring = null;
    };

    // The drag itself always tracks, reduced motion or not: that is the visitor's own finger,
    // not motion we added. What the setting removes is the flight after they let go, so the
    // panel cross-fades instead. It keeps the dragged transform until the fade is over, or
    // clearing it first would snap the panel back under the finger and fade it from there,
    // which is the one movement the setting was asking us not to make.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      // Hand the transition back before anything else, so the cross-fade the stylesheet
      // defines is the one that runs rather than the "none" the drag installed.
      d.panel.style.removeProperty('transition');
      if (closing) {
        // Same wait as the spring path: a beforeClose handler can take its own time, and
        // dropping the transform before it finishes puts the panel back under the finger.
        finish();
      } else {
        this.paint(d.panel, d.sign, 0, d.width);
        setTimeout(() => this.clear(d.panel), 220);
        this.spring = null;
      }
      return;
    }

    this.spring = this.springTo(
      d.offset,
      target,
      velocity,
      (x) => this.paint(d.panel, d.sign, x, d.width),
      finish
    );
  }
};

document.addEventListener('panel:open', (e) => PanelDrag.attach(e.detail.panel));
// A rotation or a resize changes what an open panel covers.
window.addEventListener('resize', () => {
  if (OverlayManager.stack.length) OverlayManager.syncDim();
});

/**
 *  @class
 *  @function Quantity
 */
if (!customElements.get('quantity-selector')) {
  class QuantityInput extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('.qty');
      this.step = this.input.getAttribute('step');
      this.changeEvent = new Event('change', {
        bubbles: true
      });
      // Create buttons
      this.subtract = this.querySelector('.minus');
      this.add = this.querySelector('.plus');

      // Add functionality to buttons
      this.subtract.addEventListener('click', () => this.change_quantity(-1 * this.step));
      this.add.addEventListener('click', () => this.change_quantity(1 * this.step));

    }
    connectedCallback() {
      this.classList.add('buttons_added');
      this.validateQtyRules();
    }
    change_quantity(change) {
      // Get current value
      let quantity = Number(this.input.value);

      // Ensure quantity is a valid number
      if (isNaN(quantity)) quantity = 1;

      // Check for min & max
      if (this.input.getAttribute('min') > (quantity + change)) {
        return;
      }
      if (this.input.getAttribute('max')) {
        if (this.input.getAttribute('max') < (quantity + change)) {
          return;
        }
      }
      // Change quantity
      quantity += change;

      // Ensure quantity is always a number
      quantity = Math.max(quantity, 1);

      // Output number
      this.input.value = quantity;

      this.input.dispatchEvent(this.changeEvent);

      this.validateQtyRules();
    }
    validateQtyRules() {
      const value = parseInt(this.input.value);
      if (this.input.min) {
        const min = parseInt(this.input.min);
        this.subtract.classList.toggle('disabled', value <= min);
      }
      if (this.input.max) {
        const max = parseInt(this.input.max);
        this.add.classList.toggle('disabled', value >= max);
      }
    }
  }
  customElements.define('quantity-selector', QuantityInput);
}

/**
 *  @class
 *  @function ProductCard
 */
if (!customElements.get('product-card')) {
  class ProductCard extends HTMLElement {
    constructor() {
      super();
      this._observer = null;
    }
    connectedCallback() {
      this.swatches = this.querySelector('.product-card-swatches');
      this.image = this.querySelector('.product-card--featured-image-link .product-primary-image');
      this.additional_images = this.querySelectorAll('.product-secondary-image');
      this.additional_images_nav = this.querySelectorAll('.product-secondary-images-nav li');
      this.quick_add = this.querySelector('.product-card--add-to-cart-button-simple');
      this.size_options = this.querySelector('.product-card-sizes');

      if (this.swatches) {
        this.enableSwatches(this.swatches, this.image);
      }
      if (this.additional_images) {
        this.enableAdditionalImages();
      }
      if (this.quick_add) {
        this.quick_add.addEventListener('click', this.quickAdd.bind(this));
      }
      if (this.size_options) {
        this.enableSizeOptions();
      }
      this.observeVideo();
    }
    disconnectedCallback() {
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
    }
    observeVideo() {
      var template = this.querySelector('.product-card__video-deferred');
      if (!template) return;

      var self = this;
      this._observer = new IntersectionObserver(function(entries) {
        if (entries[0].isIntersecting) {
          self._observer.disconnect();
          self._observer = null;
          template.replaceWith(template.content.cloneNode(true));
        }
      }, { rootMargin: '200px' });

      this._observer.observe(this);
    }
    enableAdditionalImages() {
      let image_length = this.additional_images.length;
      let images = this.additional_images;
      let nav = this.additional_images_nav;
      let hasNav = nav.length > 0;
      let image_container = this.querySelector('.product-card--featured-image-link');
      let lastIndex = -1;

      const mousemove = function (e) {
        let l = e.offsetX;
        let w = this.getBoundingClientRect().width;
        let sel = Math.floor((l / w) * image_length);

        if (sel === lastIndex || !images[sel]) {
          return;
        }
        if (lastIndex >= 0 && images[lastIndex]) {
          images[lastIndex].classList.remove('hover');
          if (hasNav) {
            nav[lastIndex].classList.remove('active');
          }
        }
        images[sel].classList.add('hover');
        if (hasNav) {
          nav[sel].classList.add('active');
        }
        lastIndex = sel;
      };

      const mouseleave = function () {
        if (lastIndex >= 0 && images[lastIndex]) {
          images[lastIndex].classList.remove('hover');
          if (hasNav) {
            nav[lastIndex].classList.remove('active');
          }
        }
        lastIndex = -1;
      };

      if (image_container) {
        image_container.addEventListener('touchstart', mousemove, {
          passive: true
        });
        image_container.addEventListener('touchmove', mousemove, {
          passive: true
        });
        image_container.addEventListener('touchend', mouseleave, {
          passive: true
        });
        image_container.addEventListener('mouseenter', mousemove, {
          passive: true
        });
        image_container.addEventListener('mousemove', mousemove, {
          passive: true
        });
        image_container.addEventListener('mouseleave', mouseleave, {
          passive: true
        });
      }

      window.addEventListener('load', () => {
        images.forEach((image) => {
          lazySizes.loader.unveil(image);
        });
      });

    }
    enableSwatches(swatches, image) {
      let swatch_list = swatches.querySelectorAll('.product-card-swatch'),
        org_srcset = image ? image.dataset.srcset : '';
      this.color_index = this.swatches.dataset.index;

      window.addEventListener('load', () => {
        swatch_list.forEach((swatch) => {
          let img = new Image();
          img.srcset = swatch.dataset.srcset;
          lazySizes.loader.unveil(img);
        });
      });

      swatch_list.forEach((swatch, index) => {
        swatch.addEventListener('mouseover', () => {
          swatch_list.forEach((el) => {
            el.classList.remove('active');
          });
          if (image) {
            if (swatch.dataset.srcset) {
              image.setAttribute('srcset', swatch.dataset.srcset);
            } else {
              image.setAttribute('srcset', org_srcset);
            }
          }
          if (this.size_options) {
            this.current_options[this.color_index] = swatch.querySelector('span').innerText;
            this.updateMasterId();
          }
          swatch.classList.add('active');
        });
        swatch.addEventListener('click', function (evt) {
          window.location.href = this.dataset.href;
          evt.preventDefault();
        });
      });
    }
    enableSizeOptions() {
      let size_list = this.size_options.querySelectorAll('.product-card-sizes--size'),
        featured_image = this.querySelector('.product-card--featured-image'),
        has_hover = featured_image.classList.contains('thb-hover'),
        size_parent = this.size_options.parentElement;

      this.size_index = this.size_options.dataset.index;

      this.current_options = this.size_options.dataset.options.split(',');

      this.updateMasterId();

      size_parent.addEventListener('mouseenter', () => {
        if (has_hover) {
          featured_image.classList.remove('thb-hover');
        }
      }, {
        passive: true
      });
      size_parent.addEventListener('mouseleave', () => {
        if (has_hover) {
          featured_image.classList.add('thb-hover');
        }
      }, {
        passive: true
      });
      size_list.forEach((size) => {
        size.addEventListener('click', (evt) => {
          evt.preventDefault();
          if (size.classList.contains('is-disabled')) {
            return;
          }
          this.current_options[this.size_index] = size.querySelector('span').innerText;
          this.updateMasterId();
          this.addToCart(this.currentVariant.id, size);
        });
      });
    }
    updateMasterId() {
      this.currentVariant = this.getVariantData().find((variant) => {
        return !variant.options.map((option, index) => {
          return this.current_options[index] === option;
        }).includes(false);
      });
      setTimeout(() => {
        this.setDisabled();
      }, 100);
    }
    getVariantData() {
      this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
      return this.variantData;
    }
    setDisabled() {
      const variant_data = this.getVariantData();
      if (!variant_data) {
        return true;
      }
      const size_buttons = this.size_options.querySelectorAll('.product-card-sizes--size');

      if (this.currentVariant) {
        const selected_options = this.currentVariant.options.map((value, index) => ({
          value,
          index: `option${index + 1}`
        }));
        const available_options = this.createAvailableOptionsTree(variant_data, selected_options);
        const fieldset_options = Object.values(available_options)[this.size_index];
        if (fieldset_options) {
          size_buttons.forEach((input, input_i) => {
            input.classList.toggle('is-disabled', fieldset_options[input_i].isUnavailable);
          });
        }
      } else {
        size_buttons.forEach((input) => {
          input.classList.add('is-disabled');
        });
      }
      return true;
    }
    createAvailableOptionsTree(variant_data, selected_options) {
      // Reduce variant array into option availability tree
      return variant_data.reduce((options, variant) => {

        // Check each option group (e.g. option1, option2, option3) of the variant
        Object.keys(options).forEach(index => {

          if (variant[index] === null) return;

          let entry = options[index].find(option => option.value === variant[index]);

          if (typeof entry === 'undefined') {
            // If option has yet to be added to the options tree, add it
            entry = {
              value: variant[index],
              isUnavailable: true
            };
            options[index].push(entry);
          }

          // Check how many selected option values match a variant
          const countVariantOptionsThatMatchCurrent = selected_options.reduce((count, {
            value,
            index
          }) => {
            return variant[index] === value ? count + 1 : count;
          }, 0);

          // Only enable an option if an available variant matches all but one current selected value
          if (countVariantOptionsThatMatchCurrent >= selected_options.length - 1) {
            entry.isUnavailable = entry.isUnavailable && variant.available ? false : entry.isUnavailable;
          }

          // Make sure if a variant is unavailable, disable currently selected option
          if ((!this.currentVariant || !this.currentVariant.available) && selected_options.find((option) => option.value === entry.value && index === option.index)) {
            entry.isUnavailable = true;
          }

          // First option is always enabled
          if (index === 'option1') {
            entry.isUnavailable = entry.isUnavailable && variant.available ? false : entry.isUnavailable;
          }
        });

        return options;
      }, {
        option1: [],
        option2: [],
        option3: []
      });
    }
    addToCart(variantId, button) {
      button.classList.add('loading');
      button.setAttribute('aria-disabled', true);

      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', 1);
      formData.append('sections', this.getSectionsToRender().map((section) => section.section));
      formData.append('sections_url', window.location.pathname);

      fetch(`${theme.routes.cart_add_url}`, {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/javascript'
        },
        body: formData
      })
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            return;
          }
          this.renderContents(response);
          dispatchCustomEvent('cart:item-added', {
            product: response.hasOwnProperty('items') ? response.items[0] : response
          });
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          button.classList.remove('loading');
          button.removeAttribute('aria-disabled');
        });
    }
    quickAdd(evt) {
      evt.preventDefault();
      if (this.quick_add.disabled) {
        return;
      }
      this.addToCart(this.quick_add.dataset.productId, this.quick_add);
    }
    /* CRT-003. The add and the section renders travel in ONE request, so every millisecond
       the server spends on a section spends the visitor's wait with it. This asked for
       main-cart on every add, and renderContents then threw it away on every page that is
       not /cart, because #Cart is not there to receive it. Ask for what the document can
       actually use. */
    getSectionsToRender() {
      return [{
        id: 'Cart',
        section: 'main-cart',
        selector: '.thb-cart-form'
      },
      {
        id: 'Cart-Drawer',
        section: 'cart-drawer',
        selector: '.cart-drawer'
      },
      {
        id: 'cart-drawer-toggle',
        section: 'cart-bubble',
        selector: '.thb-item-count'
      }].filter((section) => document.getElementById(section.id));
    }
    renderContents(parsedState) {
      this.getSectionsToRender().forEach((section => {
        if (!document.getElementById(section.id)) {
          return;
        }
        const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
        elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);

      }));


      const cartDrawer = document.getElementById('Cart-Drawer');
      if (cartDrawer) {
        OverlayManager.open(cartDrawer);
        dispatchCustomEvent('cart-drawer:open');
      }
    }
    getSectionInnerHTML(html, selector = '.shopify-section') {
      return new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(selector).innerHTML;
    }
  }
  customElements.define('product-card', ProductCard);
}


/**
 *  @class
 *  @function PanelClose
 */
if (!customElements.get('side-panel-close')) {
  class PanelClose extends HTMLElement {
    connectedCallback() {
      this.cc = document.querySelector('.click-capture');

      this.onClick = () => {
        OverlayManager.closeLast();
      };

      this.addEventListener('click', this.onClick);

      // It carries role="button" now, so it has to answer Enter and Space (CRT-001).
      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        this.onClick();
      });

      if (!this.cc.hasAttribute('initialized')) {
        this.cc.addEventListener('click', this.onClick);
        this.cc.setAttribute('initialized', '');
      }
    }
  }
  customElements.define('side-panel-close', PanelClose);

  // Product drawer: close images container first, then drawer
  const productDrawer = document.getElementById('Product-Drawer');
  if (productDrawer) {
    OverlayManager.registerBeforeClose(productDrawer, (done) => {
      const container = productDrawer.querySelector('.product-quick-images--container');
      if (container && container.classList.contains('active') && window.innerWidth >= 1069) {
        container.classList.remove('active');
        container.addEventListener('transitionend', () => {
          productDrawer.querySelector('#Product-Drawer-Content').innerHTML = '';
          done();
        }, { once: true });
      } else {
        container?.classList.remove('active');
        productDrawer.querySelector('#Product-Drawer-Content').innerHTML = '';
        done();
      }
    });

    document.addEventListener('panel:open', (e) => {
      if (e.detail.panel === productDrawer) {
        setTimeout(() => {
          productDrawer.querySelector('.product-quick-images--container')?.classList.add('active');
        });
      }
    });
  }

  document.addEventListener('keyup', (e) => {
    if (e.code?.toUpperCase() === 'ESCAPE') {
      OverlayManager.closeLast();
    }
  });
}
/**
 *  @class
 *  @function CartDrawer
 */
if (!customElements.get('cart-drawer')) {
  class CartDrawer extends HTMLElement {

    constructor() {
      super();
    }

    connectedCallback() {
      let button = document.getElementById('cart-drawer-toggle');
      if (!button) return;

      // Add functionality to buttons
      button.addEventListener('click', (e) => {
        e.preventDefault();
        OverlayManager.open(this);
        dispatchCustomEvent('cart-drawer:open');
      });

      document.addEventListener('panel:open', (e) => {
        if (e.detail.panel === this) {
          setTimeout(() => {
            this.querySelector('.product-recommendations--full')?.classList.add('active');
          });
        }
      });

      this.debouncedOnChange = debounce((event) => {
        this.onChange(event);
      }, 300);

      document.addEventListener('cart:refresh', (event) => {
        this.refresh();
      });

      this.addEventListener('change', this.debouncedOnChange.bind(this));

      OverlayManager.registerBeforeClose(this, (done) => {
        const recs = this.querySelector('.product-recommendations--full');
        if (recs && recs.classList.contains('active') && window.innerWidth >= 1069) {
          recs.classList.remove('active');
          recs.addEventListener('transitionend', () => done(), { once: true });
        } else {
          recs?.classList.remove('active');
          done();
        }
      });

    }
    onChange(event) {
      if (event.target.classList.contains('qty')) {
        this.updateQuantity(event.target.dataset.index, event.target.value);
      }
    }
    getSectionsToRender() {
      return [{
        id: 'Cart-Drawer',
        section: 'cart-drawer',
        selector: '.cart-drawer'
      },
      {
        id: 'cart-drawer-toggle',
        section: 'cart-bubble',
        selector: '.thb-item-count'
      }];
    }
    getSectionInnerHTML(html, selector) {
      return new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(selector).innerHTML;
    }
    updateQuantity(line, quantity) {
      this.querySelector(`#CartDrawerItem-${line}`)?.classList.add('thb-loading');
      const body = JSON.stringify({
        line,
        quantity,
        sections: this.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname
      });

      dispatchCustomEvent('line-item:change:start', {
        quantity: quantity
      });
      this.querySelector('.product-recommendations--full')?.classList.remove('active');

      fetch(`${theme.routes.cart_change_url}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': `application/json`
        },
        ...{
          body
        }
      })
        .then((response) => {
          return response.text();
        })
        .then((state) => {
          const parsedState = JSON.parse(state);

          this.getSectionsToRender().forEach((section => {
            const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);

            if (parsedState.sections) {
              elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
            }
          }));

          dispatchCustomEvent('line-item:change:end', {
            quantity: quantity,
            cart: parsedState
          });

          this.querySelector(`#CartDrawerItem-${line}`)?.classList.remove('thb-loading');
        })
        .catch((error) => {
          // Never leave the row stuck in its loading state — any failure
          // here previously presented as an infinite spinner.
          console.error(error);
          this.querySelector(`#CartDrawerItem-${line}`)?.classList.remove('thb-loading');
        });
    }
    refresh() {
      this.querySelector('.product-recommendations--full')?.classList.remove('active');
      let sections = 'cart-drawer,cart-bubble';
      fetch(`${window.location.pathname}?sections=${sections}`)
        .then((response) => {
          return response.text();
        })
        .then((state) => {
          const parsedState = JSON.parse(state);

          this.getSectionsToRender().forEach((section => {
            const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);

            elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState[section.section], section.selector);
          }));

        });
    }
  }
  customElements.define('cart-drawer', CartDrawer);
}

/**
 *  @class
 *  @function ResizeSelect
 */
if (!customElements.get('resize-select')) {
  class ResizeSelect extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.select = this.querySelector('select');
      this.addEventListeners();
    }
    addEventListeners() {
      this.addEventListener('change', this.resizeSelect.bind(this));
      window.addEventListener('load', this.resizeSelect.bind(this));
      const details = this.closest('details');
      if (details) {
        details.addEventListener('toggle', () => {
          if (details.open) this.resizeSelect();
        });
      }
      this.resizeSelect();
    }
    resizeSelect() {
      let tempOption = document.createElement('option');
      tempOption.textContent = this.select.selectedOptions[0].textContent;

      let tempSelect = document.createElement('select');
      tempSelect.style.cssText = 'visibility:hidden;position:fixed;pointer-events:none';
      tempSelect.appendChild(tempOption);
      this.after(tempSelect);

      requestAnimationFrame(() => {
        if (tempSelect.clientWidth > 0) {
          this.select.style.width = `${tempSelect.clientWidth + 10}px`;
        }
        tempSelect.remove();
      });
    }
  }
  customElements.define('resize-select', ResizeSelect);
}



/**
 *  @class
 *  @function QuickView
 */
if (!customElements.get('quick-view')) {
  const quickViewCache = new Map();

  class QuickView extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.drawer = document.getElementById('Product-Drawer');
      this.addEventListener('click', this.handleClick.bind(this));
      // The control could be seen on hover and pressed with a mouse, but a keyboard could
      // never reach it (tabindex -1) and Enter did nothing, so the choice pointer users get
      // was not offered to anyone else (GLB-006). On touch it is display: none, and a hidden
      // element takes no focus, so this costs nothing there.
      this.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        this.handleClick(e);
      });
    }
    handleClick(e) {
      e.preventDefault();
      let productHandle = this.dataset.productHandle;
      if (!productHandle) {
        return;
      }
      let rootUrl = theme.routes.root_url.replace(/\/+$/, '');
      let href = `${rootUrl}/products/${productHandle}?view=quick-view`;

      if (this.classList.contains('loading')) {
        return;
      }
      this.classList.add('loading');

      // Abort any in-flight quick-view fetch
      if (QuickView.activeController) {
        QuickView.activeController.abort();
      }
      let controller = new AbortController();
      QuickView.activeController = controller;

      // Serve from cache if available
      if (quickViewCache.has(productHandle)) {
        this.classList.remove('loading');
        this.renderQuickView(quickViewCache.get(productHandle), href, productHandle);
        return;
      }

      fetch(href, {
        method: 'GET',
        signal: controller.signal
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Quick view fetch failed: ${response.status}`);
          }
          return response.text();
        })
        .then(text => {
          let parsed = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('#Product-Drawer-Content');

          if (!parsed) {
            throw new Error('Quick view content not found in response');
          }
          let sectionInnerHTML = parsed.innerHTML;
          quickViewCache.set(productHandle, sectionInnerHTML);
          this.renderQuickView(sectionInnerHTML, href, productHandle);
        })
        .catch((error) => {
          if (error.name !== 'AbortError') {
            console.error(error);
          }
        })
        .finally(() => {
          this.classList.remove('loading');
        });
    }
    _reloadScripts(container) {
      let scripts = container.querySelectorAll('script');
      let head = document.head;
      scripts.forEach((oldScript) => {
        let newScript = document.createElement('script');
        Array.from(oldScript.attributes).forEach((attr) => {
          newScript.setAttribute(attr.name, attr.value);
        });
        if (oldScript.src) {
          if (document.querySelector(`script[src="${oldScript.src}"]`)) {
            return;
          }
        } else {
          newScript.textContent = oldScript.textContent;
        }
        if (oldScript.parentNode) {
          oldScript.parentNode.replaceChild(newScript, oldScript);
        } else {
          head.appendChild(newScript);
        }
      });
    }
    renderQuickView(sectionInnerHTML, href, productHandle) {
      if (!sectionInnerHTML) {
        return;
      }
      let drawerContent = this.drawer.querySelector('#Product-Drawer-Content');
      drawerContent.innerHTML = sectionInnerHTML;

      this._reloadScripts(drawerContent);

      requestAnimationFrame(() => {
        if (typeof Shopify !== 'undefined' && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }
        if (window.ProductModel) {
          window.ProductModel.loadShopifyXR();
        }
      });

      OverlayManager.open(this.drawer);

      dispatchCustomEvent('quick-view:open', {
        productUrl: href,
        productHandle: productHandle
      });
      addIdToRecentlyViewed(productHandle);
    }
  }
  QuickView.activeController = null;
  customElements.define('quick-view', QuickView);
}

/**
 *  @class
 *  @function SidePanelContentTabs
 */
if (!customElements.get('side-panel-content-tabs')) {
  class SidePanelContentTabs extends HTMLElement {
    constructor() {
      super();
      this.buttons = this.querySelectorAll('button');
      this.panels = this.parentElement.querySelectorAll('.side-panel-content--tab-panel');
    }
    connectedCallback() {
      this.setupButtonObservers();
    }
    setupButtonObservers() {
      this.buttons.forEach((item, i) => {
        item.addEventListener('click', (e) => {
          this.toggleActiveClass(i);
        });
      });
    }
    toggleActiveClass(i) {
      this.buttons.forEach((button) => {
        button.classList.remove('tab-active');
      });
      this.buttons[i].classList.add('tab-active');

      this.panels.forEach((panel) => {
        panel.classList.remove('tab-active');
      });
      this.panels[i].classList.add('tab-active');
    }
  }

  customElements.define('side-panel-content-tabs', SidePanelContentTabs);
}

/**
 *  @class
 *  @function CollapsibleRow
 */
if (!customElements.get('collapsible-row')) {
  // https://css-tricks.com/how-to-animate-the-details-element/
  class CollapsibleRow extends HTMLElement {
    constructor() {
      super();

      this.details = this.querySelector('details');
      this.summary = this.querySelector('summary');
      this.content = this.querySelector('.collapsible__content');
      this.accordionGroup = this.dataset.accordionGroup || '';
      this.prefersReducedMotion = Boolean(
        window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
      );

      // Store the animation object (so we can cancel it if needed)
      this.animation = null;
      // Store if the element is closing
      this.isClosing = false;
      // Store if the element is expanding
      this.isExpanding = false;
    }
    connectedCallback() {
      if (!this.details || !this.summary || !this.content) return;

      this.enforceExclusiveInitialState();
      this.syncAccessibilityState();
      this.setListeners();
      this.details.addEventListener('toggle', () => this.syncAccessibilityState());
    }
    setListeners() {
      // Opt-out of the WAAPI height animation. Used by horizontal facet
      // dropdowns, where the panel is floating (position: absolute) and
      // animating the parent <details> height would push surrounding layout.
      if (this.dataset.skipCollapse === 'true') return;
      this.summary.addEventListener('click', (e) => this.onClick(e));
    }
    getGroupRows() {
      if (!this.accordionGroup) return [];

      return Array.from(document.querySelectorAll('collapsible-row[data-accordion-group]'))
        .filter((row) => row.dataset.accordionGroup === this.accordionGroup);
    }
    enforceExclusiveInitialState() {
      if (!this.accordionGroup) return;

      const openRows = this.getGroupRows()
        .filter((row) => row.querySelector('details')?.open);

      openRows.slice(1).forEach((row) => {
        const details = row.querySelector('details');
        const summary = row.querySelector('summary');

        details.open = false;
        summary?.setAttribute('aria-expanded', 'false');
      });
    }
    closeGroupRows() {
      this.getGroupRows().forEach((row) => {
        if (row === this || row.isClosing) return;

        const details = row.querySelector('details');
        if (!details?.open && !row.isExpanding) return;

        if (typeof row.shrink === 'function') {
          row.shrink();
        } else {
          details.open = false;
          row.querySelector('summary')?.setAttribute('aria-expanded', 'false');
        }
      });
    }
    syncAccessibilityState() {
      const isOpen = this.details.open;

      this.summary.setAttribute('aria-expanded', String(isOpen));
      if (this.content.id) {
        this.summary.setAttribute('aria-controls', this.content.id);
      }
    }
    onClick(e) {
      // Stop default behaviour from the browser
      e.preventDefault();
      // Add an overflow on the <details> to avoid content overflowing
      this.details.style.overflow = 'hidden';
      // Check if the element is being closed or is already closed
      if (this.isClosing || !this.details.open) {
        this.closeGroupRows();
        this.open();
        // Check if the element is being openned or is already open
      } else if (this.isExpanding || this.details.open) {
        this.shrink();
      }
    }
    shrink() {
      // Set the element as "being closed"
      this.isClosing = true;
      this.classList.add('is-closing');
      this.classList.remove('is-expanding');
      this.details.style.overflow = 'hidden';

      // Store the current height of the element
      const startHeight = `${this.details.offsetHeight}px`;
      // Calculate the height of the summary
      const endHeight = `${this.summary.offsetHeight}px`;

      if (this.prefersReducedMotion) {
        this.onAnimationFinish(false);
        return;
      }

      // If there is already an animation running
      if (this.animation) {
        // Cancel the current animation
        this.animation.cancel();
      }

      // Start a WAAPI animation
      this.animation = this.details.animate({
        // Set the keyframes from the startHeight to endHeight
        height: [startHeight, endHeight]
      }, {
        duration: 250,
        easing: 'cubic-bezier(0.104, 0.204, 0.492, 1)'
      });

      // When the animation is complete, call onAnimationFinish()
      this.animation.onfinish = () => this.onAnimationFinish(false);
      // If the animation is cancelled, isClosing variable is set to false
      this.animation.oncancel = () => this.isClosing = false;
    }

    open() {
      // Apply a fixed height on the element
      this.details.style.height = `${this.details.offsetHeight}px`;
      // Force the [open] attribute on the details element
      this.details.open = true;
      this.querySelectorAll('max-height').forEach((maxHeight) => {
        if (typeof maxHeight.checkVisible === 'function') {
          maxHeight.checkVisible();
        }
      });
      this.classList.add('is-expanding');
      this.classList.remove('is-closing');
      this.syncAccessibilityState();

      if (this.prefersReducedMotion) {
        this.onAnimationFinish(true);
        return;
      }

      // Wait for the next frame to call the expand function
      window.requestAnimationFrame(() => this.expand());
    }

    expand() {
      // Set the element as "being expanding"
      this.isExpanding = true;
      // Get the current fixed height of the element
      const startHeight = `${this.details.offsetHeight}px`;
      // Calculate the open height of the element (summary height + content height)
      const endHeight = `${this.summary.offsetHeight + this.content.offsetHeight}px`;

      // If there is already an animation running
      if (this.animation) {
        // Cancel the current animation
        this.animation.cancel();
      }

      // Start a WAAPI animation
      this.animation = this.details.animate({
        // Set the keyframes from the startHeight to endHeight
        height: [startHeight, endHeight]
      }, {
        duration: 250,
        easing: 'cubic-bezier(0.104, 0.204, 0.492, 1)'
      });
      // When the animation is complete, call onAnimationFinish()
      this.animation.onfinish = () => this.onAnimationFinish(true);
      // If the animation is cancelled, isExpanding variable is set to false
      this.animation.oncancel = () => this.isExpanding = false;
    }

    onAnimationFinish(open) {
      // Set the open attribute based on the parameter
      this.details.open = open;
      this.syncAccessibilityState();
      // Clear the stored animation
      this.animation = null;
      // Reset isClosing & isExpanding
      this.isClosing = false;
      this.isExpanding = false;
      this.classList.remove('is-closing', 'is-expanding');
      // Remove the overflow hidden and the fixed height
      this.details.style.height = this.details.style.overflow = '';
    }
  }
  customElements.define('collapsible-row', CollapsibleRow);
}

/**
 *  @function addIdToRecentlyViewed
 */
function addIdToRecentlyViewed(handle) {

  if (!handle) {
    let product = document.querySelector('.thb-product-detail');

    if (product) {
      handle = product.dataset.handle;
    }
  }
  if (!handle) {
    return;
  }
  if (window.localStorage) {
    let recentIds = window.localStorage.getItem('recently-viewed');
    if (recentIds != 'undefined' && recentIds != null) {
      window.recentlyViewedIds = JSON.parse(recentIds);
    }
  }
  // Remove current product if already in recently viewed array
  var i = window.recentlyViewedIds.indexOf(handle);

  if (i > -1) {
    window.recentlyViewedIds.splice(i, 1);
  }

  // Add id to array
  window.recentlyViewedIds.unshift(handle);

  if (window.localStorage) {
    window.localStorage.setItem('recently-viewed', JSON.stringify(window.recentlyViewedIds));
  }
}

// iOS Safari applies :active only when a touchstart listener exists on the element or one of its
// ancestors. One passive listener on the document gives every button its pressed state on touch
// (apple-design §1 Response); it does nothing else and never blocks scrolling.
document.addEventListener('touchstart', () => {}, { passive: true });
